/**
 * Brand configuration: the pure policy behind the columns a brand profile
 * carries besides its assets. Every reader here tolerates loose jsonb, because
 * the brand-settings UI writes these columns through Hasura with no backend
 * validation.
 *
 * Kept free of database and other server-only imports so the brand-settings UI
 * can import it directly. Writes live in `db.ts`, orchestration in `service.ts`.
 */

import type { AssignmentSource } from "../llm/lifecycle-studio/dynamic-content/assignment-source";
import type { EmailStyleConfiguration } from "./types";

export const DEFAULT_MOBILE_BREAKPOINT_WIDTH = 600;

export const MOBILE_BREAKPOINT_PRESETS = [600, 640, 680] as const;

export const MIN_MOBILE_BREAKPOINT_WIDTH = 320;
export const MAX_MOBILE_BREAKPOINT_WIDTH = 1024;

/** Desktop content column width the standalone component wrapper caps at. */
export const DEFAULT_CONTENT_WIDTH_PX = 600;

export const CONTENT_WIDTH_PRESETS = [600, 640, 680] as const;

export const MIN_CONTENT_WIDTH_PX = 320;
// Match the Figma import cap (`FIGMA_TARGET_EMAIL_WIDTH_MAX`) — a wider content
// width would fail LCM Figma staging.
export const MAX_CONTENT_WIDTH_PX = 900;

/** Fully-resolved config: every field is present. */
export type ResolvedEmailStyleConfiguration = {
  mobileBreakpointWidth: number;
  contentWidthPx: number;
  underlineLinks: boolean;
};

const DEFAULT_EMAIL_STYLE_CONFIGURATION: ResolvedEmailStyleConfiguration = {
  mobileBreakpointWidth: DEFAULT_MOBILE_BREAKPOINT_WIDTH,
  contentWidthPx: DEFAULT_CONTENT_WIDTH_PX,
  underlineLinks: false,
};

/**
 * Coerce one loose jsonb numeric field into an in-range value: `fallback` when
 * it's missing or non-finite, otherwise clamped into [min, max].
 *
 * Clamping (not falling back to the default) for out-of-range values is
 * load-bearing: this resolver is the single source of truth read by the
 * brand-settings UI, the config object, AND every standalone-render path. A
 * stored value just above the max (e.g. 950 when max is 900) should resolve to
 * the nearest valid width (900), not silently snap back to the 600 default —
 * which would surprise a workspace whose jsonb still holds the larger value.
 */
function resolveWidthField(
  value: unknown,
  { min, max, fallback }: { min: number; max: number; fallback: number },
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  const int = Math.round(value);
  return Math.min(max, Math.max(min, int));
}

/**
 * Coerce the loose jsonb value into a fully-populated config, falling back to
 * defaults for missing, malformed, or out-of-range fields. Safe for both backend
 * readers and the frontend settings UI, and authoritative for rendering.
 */
export function resolveEmailStyleConfiguration(
  value: unknown,
): ResolvedEmailStyleConfiguration {
  if (!value || typeof value !== "object") {
    return { ...DEFAULT_EMAIL_STYLE_CONFIGURATION };
  }

  const raw = value as Partial<
    Record<keyof ResolvedEmailStyleConfiguration, unknown>
  >;

  return {
    mobileBreakpointWidth: resolveWidthField(raw.mobileBreakpointWidth, {
      min: MIN_MOBILE_BREAKPOINT_WIDTH,
      max: MAX_MOBILE_BREAKPOINT_WIDTH,
      fallback: DEFAULT_MOBILE_BREAKPOINT_WIDTH,
    }),
    contentWidthPx: resolveWidthField(raw.contentWidthPx, {
      min: MIN_CONTENT_WIDTH_PX,
      max: MAX_CONTENT_WIDTH_PX,
      fallback: DEFAULT_CONTENT_WIDTH_PX,
    }),
    underlineLinks: raw.underlineLinks === true,
  };
}

/** The shape we persist to the jsonb column. */
export function toPersistedConfig(
  config: ResolvedEmailStyleConfiguration,
): EmailStyleConfiguration {
  return {
    mobileBreakpointWidth: config.mobileBreakpointWidth,
    contentWidthPx: config.contentWidthPx,
    ...(config.underlineLinks ? { underlineLinks: true } : {}),
  };
}

/**
 * `brand_profiles.image_guardrails`, the brand's image rules. jsonb rather
 * than a column per rule so a new toggle needs no migration; an empty object
 * means no guardrails, which is what an untouched brand has.
 *
 * Every flag is permission-shaped and defaults to allowed, so a brand that
 * never opened the settings behaves exactly as it did before guardrails
 * existed, whatever keys are added later.
 */
export type ImageGuardrails = {
  /** False blocks AI-generated and AI-edited imagery in ads. */
  allowImageGeneration: boolean;
};

export const DEFAULT_IMAGE_GUARDRAILS: ImageGuardrails = {
  allowImageGeneration: true,
};

/**
 * Reads the jsonb column. A missing, malformed, or wrong-typed value falls
 * back to allowed, so bad data cannot silently lock a brand out of
 * generating imagery.
 */
export function parseImageGuardrails(value: unknown): ImageGuardrails {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return DEFAULT_IMAGE_GUARDRAILS;
  }
  const raw = (value as Record<string, unknown>).allowImageGeneration;
  return {
    allowImageGeneration: typeof raw === "boolean" ? raw : true,
  };
}

/**
 * Merges a change onto the stored value so writing one flag cannot drop the
 * others once there is more than one.
 */
export function withImageGuardrail(
  stored: unknown,
  change: Partial<ImageGuardrails>,
): ImageGuardrails {
  return { ...parseImageGuardrails(stored), ...change };
}

const ASSIGNMENT_SOURCES: ReadonlySet<AssignmentSource> = new Set([
  "audience_schema",
  "esp_profile",
]);

/**
 * `brand_profiles.default_assignment_source`. Null means inherit: callers
 * derive from the workspace default, then the resolved email provider.
 */
export function parseAssignmentSource(
  value: string | null | undefined,
): AssignmentSource | null {
  // Empty string is how some clients spell clear/inherit (Select isClearable).
  if (value == null || value === "") return null;
  if (ASSIGNMENT_SOURCES.has(value as AssignmentSource)) {
    return value as AssignmentSource;
  }
  throw new Error(
    `Unknown assignment source ${value}. Expected audience_schema, esp_profile, or null to inherit.`,
  );
}
